// @ts-check
// Campo do cartão do kanban com edição no próprio cartão (doc 06, seção 6.4): o clique no valor abre o mesmo editor da
// tabela; sim/não alterna e a nota escolhe a estrela. Campo somente leitura (papel, sistema ou data da visita travada
// depois da visita realizada) só mostra o valor. A gravação passa pelo diálogo da etapa, como no resto da tela.
import { html } from 'htm/preact';
import { FIELD_TYPE, getFieldIcon, isFieldReadOnly } from '../../lib/field-metadata.js';
import { canUpdateObjectRecords } from '../../lib/permissions.js';
import { Icon } from '../../ui/icon.js';
import { translateMetadataLabel } from '../../i18n/index.js';
import { FieldDisplay, RatingStars, ratingToNumber } from './field-display.js';
import { FieldInput, getInputOnlyPatch, isFieldEditableInline } from './field-input.js';
import { isFieldLockedByStage } from './stage-transition.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../lib/field-metadata.js').FieldMetadata} FieldMetadata
 * @typedef {import('./field-input.js').FieldPatch} FieldPatch
 */

/**
 * O campo pode ser editado no cartão por este membro, neste registro.
 * @param {ObjectMetadata} objectMetadata @param {FieldMetadata} field @param {any} record
 */
export function isCardFieldEditable(objectMetadata, field, record) {
  return canUpdateObjectRecords(objectMetadata) && !isFieldReadOnly(field) && isFieldEditableInline(field)
    && field.type !== FIELD_TYPE.RICH_TEXT && !isFieldLockedByStage(objectMetadata, field, record);
}

/**
 * @param {{ objectMetadata: ObjectMetadata, field: FieldMetadata, record: any, isEditing: boolean, children?: any,
 *   onStartEdit: () => void, onStopEdit: () => void, onCommit: (patch: FieldPatch) => void }} props
 *   children: valor próprio (lista de alunos) no lugar do FieldDisplay
 */
export function BoardCardField({ objectMetadata, field, record, isEditing, children, onStartEdit, onStopEdit, onCommit }) {
  const isEditable = !children && isCardFieldEditable(objectMetadata, field, record);
  const label = translateMetadataLabel(field.label);
  const startEdit = (/** @type {MouseEvent} */ event) => {
    if (!isEditable) return;
    // links do valor (e-mail, telefone, registro relacionado) continuam abrindo
    if (/** @type {HTMLElement} */ (event.target).closest('a, .rating-star')) {
      event.stopPropagation();
      return;
    }
    event.stopPropagation();
    if (field.type === FIELD_TYPE.BOOLEAN) {
      onCommit(getInputOnlyPatch(field, record));
      return;
    }
    if (field.type === FIELD_TYPE.RATING) return;
    onStartEdit();
  };
  return html`<div
    class="board-card-field"
    data-related-list=${children ? 'true' : undefined}
    data-editable=${isEditable ? 'true' : undefined}
    data-editing=${isEditing ? 'true' : undefined}
    data-field-name=${field.name}
    title=${label}
    onClick=${startEdit}
  >
    <span class="board-card-field-icon"><${Icon} name=${getFieldIcon(field)} size=${16} /></span>
    <span class="board-card-field-value">
      ${children ?? (field.type === FIELD_TYPE.RATING && isEditable
        ? html`<${RatingStars} value=${ratingToNumber(record[field.name])} onSelect=${(/** @type {number} */ rating) => onCommit(getInputOnlyPatch(field, record, rating))} />`
        : html`<${FieldDisplay} field=${field} record=${record} emptyLabel=${label} />`)}
    </span>
    ${isEditing
      ? html`<${FieldInput}
          field=${field}
          record=${record}
          onCommit=${(/** @type {FieldPatch} */ patch) => {
            onStopEdit();
            onCommit(patch);
          }}
          onCancel=${onStopEdit}
        />`
      : null}
  </div>`;
}
